<div class="row spacing-20">
  <!-- Loading State -->
  @if (loading) {
    <div class="col-12">
      <div class="card">
        <div class="card-body text-center py-20">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ 'trainer.loading' | translate }}</span>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Recommend Trainer Card - Always visible at top -->
  @if (!loading) {
    <div class="col-12 mb-4">
      <div class="card recommend-card" (click)="openRecommendModal()">
        <div class="card-body d-flex align-items-center cursor-pointer">
          <div class="recommend-icon me-3">
            <i class="fa fa-user-friends text-success"></i>
          </div>
          <div class="flex-grow-1">
            <h6 class="mb-1">{{ 'trainer.recommendTrainer' | translate }}</h6>
            <p class="text-muted mb-0 fs-7">{{ 'trainer.recommendDescription' | translate }}</p>
          </div>
          <i class="fa fa-chevron-right text-muted"></i>
        </div>
      </div>
    </div>
  }

  <!-- STATE 1: Not a trainer (or cancelled) and no pending application - show Apply options -->
  @if (!loading && !isActiveTrainer() && !hasPendingApplication() && !isRejectedApplication()) {
    <div class="col-12">
      <div class="card trainer-promo-card">
        <div class="card-body text-center py-10">
          <div class="trainer-icon-container mb-5">
            <i class="fa fa-chalkboard-teacher"></i>
          </div>
          <h2 class="mb-3">{{ 'trainer.becomeTrainer' | translate }}</h2>
          <p class="text-muted mb-5 max-w-500 mx-auto">
            {{ 'trainer.promoText' | translate }}
          </p>

          <div class="benefits-grid mb-8">
            <div class="benefit-item">
              <i class="fa fa-eye text-primary"></i>
              <h5>{{ 'trainer.benefitVisibility' | translate }}</h5>
              <p>{{ 'trainer.benefitVisibilityDesc' | translate }}</p>
            </div>
            <div class="benefit-item">
              <i class="fa fa-id-card text-primary"></i>
              <h5>{{ 'trainer.benefitProfile' | translate }}</h5>
              <p>{{ 'trainer.benefitProfileDesc' | translate }}</p>
            </div>
            <div class="benefit-item">
              <i class="fa fa-envelope text-primary"></i>
              <h5>{{ 'trainer.benefitContact' | translate }}</h5>
              <p>{{ 'trainer.benefitContactDesc' | translate }}</p>
            </div>
            <div class="benefit-item">
              <i class="fa fa-images text-primary"></i>
              <h5>{{ 'trainer.benefitGallery' | translate }}</h5>
              <p>{{ 'trainer.benefitGalleryDesc' | translate }}</p>
            </div>
          </div>

          <div class="mt-4">
            <button class="btn btn-primary btn-lg" (click)="openApplicationModal()">
              <i class="fa fa-paper-plane me-2"></i>
              {{ 'trainer.applyNow' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- STATE 2: Pending Application - show status -->
  @if (!loading && hasPendingApplication()) {
    <div class="col-12">
      <div class="card border-warning">
        <div class="card-body">
          <div class="d-flex align-items-start">
            <div class="status-icon-pending me-4">
              <i class="fa fa-clock"></i>
            </div>
            <div class="flex-grow-1">
              <h4 class="mb-2">{{ 'trainer.applicationPending' | translate }}</h4>
              <p class="text-muted mb-3">{{ 'trainer.applicationPendingDesc' | translate }}</p>
              @if (getApplication()?.created_at) {
                <p class="text-muted fs-7 mb-0">
                  <i class="fa fa-calendar me-2"></i>
                  {{ 'trainer.submittedOn' | translate }}: {{ formatDate(getApplication()!.created_at) }}
                </p>
              }
            </div>
          </div>
          <div class="mt-4 pt-3 border-top">
            <button class="btn btn-light-danger btn-sm" (click)="withdrawApplication()" [disabled]="withdrawing">
              @if (withdrawing) {
                <span class="spinner-border spinner-border-sm me-2"></span>
              }
              {{ 'trainer.withdrawApplication' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- STATE 3: Rejected Application - show rejection and apply again -->
  @if (!loading && isRejectedApplication() && !hasPendingApplication()) {
    <div class="col-12">
      <div class="card border-danger">
        <div class="card-body">
          <div class="d-flex align-items-start">
            <div class="status-icon-rejected me-4">
              <i class="fa fa-times-circle"></i>
            </div>
            <div class="flex-grow-1">
              <h4 class="mb-2">{{ 'trainer.applicationRejected' | translate }}</h4>
              @if (getApplication()?.rejection_reason) {
                <p class="text-muted mb-3">
                  <strong>{{ 'trainer.rejectionReason' | translate }}:</strong> {{ getApplication()!.rejection_reason }}
                </p>
              }
            </div>
          </div>
          <div class="mt-4 pt-3 border-top">
            <button class="btn btn-primary" (click)="openApplicationModal()">
              <i class="fa fa-redo me-2"></i>
              {{ 'trainer.applyAgain' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- STATE 4: Active Trainer - Profile Management -->
  @if (!loading && isActiveTrainer()) {
    <!-- Status Card -->
    <div class="col-12 mb-5">
      <div class="card border-success">
        <div class="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0">
              <i class="fa fa-check-circle text-success me-2"></i>
              {{ 'trainer.activeTrainer' | translate }}
            </h5>
          </div>
          <button class="btn btn-light-danger" (click)="cancelSubscription()">
            {{ 'trainer.cancelSubscription' | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- Profile Form -->
    <div class="col-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">
            <i class="fa fa-user-edit me-2 text-primary"></i>
            {{ 'trainer.profileTitle' | translate }}
          </h4>
        </div>
        <div class="card-body">
          <!-- Specializations -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <p class="text-muted fs-7 mb-3">{{ 'trainer.specializationsHint' | translate }}</p>
            <div class="specializations-selector">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isSpecializationSelected(spec)"
                  (click)="toggleSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
            <input
              type="text"
              class="form-control mt-3"
              [(ngModel)]="trainerSpecializations"
              [placeholder]="'trainer.specializationsPlaceholder' | translate"
            />
          </div>

          <!-- Experience -->
          <div class="mb-5">
            <app-input-component
              [label]="'trainer.experience' | translate"
              [placeholder]="'trainer.experiencePlaceholder' | translate"
              [type]="'text'"
              [(value)]="trainerExperience"
            />
          </div>

          <!-- Description -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.aboutYou' | translate }}</label>
            <app-markdown-editorini
              [value]="trainerDescription"
              (valueChange)="trainerDescription = $event"
            />
          </div>

          <!-- Certificates -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.certificates' | translate }}</label>
            <p class="text-muted fs-7 mb-2">{{ 'trainer.certificatesHint' | translate }}</p>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="trainerCertificates"
              [placeholder]="'trainer.certificatesPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Achievements -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.achievements' | translate }}</label>
            <p class="text-muted fs-7 mb-2">{{ 'trainer.achievementsHint' | translate }}</p>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="trainerAchievements"
              [placeholder]="'trainer.achievementsPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Social Media -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.socialMedia' | translate }}</label>
            <p class="text-muted fs-7 mb-3">{{ 'trainer.socialMediaHint' | translate }}</p>
            <div class="row g-3">
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="fa fa-globe"></i></span>
                  <input type="url" class="form-control" [(ngModel)]="trainerWebsiteUrl" placeholder="https://www.example.com" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="fab fa-facebook" style="color: #1877F2"></i></span>
                  <input type="url" class="form-control" [(ngModel)]="trainerFacebookUrl" placeholder="https://facebook.com/..." />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="fab fa-instagram" style="color: #E4405F"></i></span>
                  <input type="url" class="form-control" [(ngModel)]="trainerInstagramUrl" placeholder="https://instagram.com/..." />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="fab fa-youtube" style="color: #FF0000"></i></span>
                  <input type="url" class="form-control" [(ngModel)]="trainerYoutubeUrl" placeholder="https://youtube.com/..." />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text"><i class="fab fa-tiktok"></i></span>
                  <input type="url" class="form-control" [(ngModel)]="trainerTiktokUrl" placeholder="https://tiktok.com/@..." />
                </div>
              </div>
            </div>
          </div>

          <!-- Photos Section -->
          <div class="mb-5">
            <label class="form-label fw-bold">{{ 'trainer.photos' | translate }}</label>
            <p class="text-muted fs-7 mb-3">{{ 'trainer.photosHint' | translate }}</p>

            <!-- Upload Button -->
            <div class="upload-area mb-4">
              <input
                type="file"
                id="photoUpload"
                class="d-none"
                accept="image/*"
                multiple
                (change)="onFilesSelected($event)"
              />
              <label for="photoUpload" class="upload-label" [class.uploading]="uploadingImage">
                @if (uploadingImage) {
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  {{ 'trainer.uploading' | translate }} {{ uploadProgress }}...
                } @else {
                  <i class="fa fa-cloud-upload-alt me-2"></i>
                  {{ 'trainer.uploadPhotos' | translate }}
                }
              </label>
            </div>

            <!-- Photos Grid -->
            @if (loadingImages) {
              <div class="text-center py-5">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ 'trainer.loading' | translate }}</span>
                </div>
              </div>
            } @else if (images.length > 0) {
              <div class="photos-grid">
                @for (image of images; track image.image_id) {
                  <div class="photo-item">
                    <img [src]="image.image_url" [alt]="image.caption || 'Trainer photo'" />
                    <button class="delete-btn" (click)="deleteImage(image)" [title]="'trainer.deletePhoto' | translate">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                }
              </div>
            } @else {
              <div class="text-center py-5 bg-light rounded">
                <i class="fa fa-images fs-2x text-muted mb-3"></i>
                <p class="text-muted mb-0">{{ 'trainer.noPhotos' | translate }}</p>
              </div>
            }
          </div>

          <!-- Save Button -->
          <div class="text-end">
            <button class="btn btn-primary" [disabled]="saving" (click)="saveProfile()">
              @if (saving) {
                <span class="spinner-border spinner-border-sm me-2"></span>
                {{ 'trainer.saving' | translate }}
              } @else {
                <i class="fa fa-save me-2"></i>
                {{ 'trainer.saveProfile' | translate }}
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div>

<!-- Application Modal -->
@if (showApplicationModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-paper-plane me-2 text-primary"></i>
            {{ 'trainer.applicationTitle' | translate }}
          </h5>
          <button type="button" class="btn-close" (click)="closeApplicationModal()"></button>
        </div>
        <div class="modal-body">
          <!-- About You -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.aboutYou' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="4"
              [(ngModel)]="applicationForm.trainer_description"
              [placeholder]="'trainer.aboutYouPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Experience -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.experience' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="applicationForm.trainer_experience"
              [placeholder]="'trainer.experiencePlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Specializations -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <div class="specializations-selector mb-2">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isApplicationSpecializationSelected(spec)"
                  (click)="toggleApplicationSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
          </div>

          <!-- Certificates -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.certificates' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="applicationForm.trainer_certificates"
              [placeholder]="'trainer.certificatesPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Achievements -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.achievements' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="applicationForm.trainer_achievements"
              [placeholder]="'trainer.achievementsPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Contact Info -->
          <div class="row">
            <div class="col-md-6 mb-4">
              <label class="form-label fw-bold">{{ 'trainer.contactEmail' | translate }}</label>
              <input
                type="email"
                class="form-control"
                [(ngModel)]="applicationForm.contact_email"
                [placeholder]="'trainer.contactEmailPlaceholder' | translate"
              />
            </div>
            <div class="col-md-6 mb-4">
              <label class="form-label fw-bold">{{ 'trainer.contactPhone' | translate }}</label>
              <input
                type="tel"
                class="form-control"
                [(ngModel)]="applicationForm.contact_phone"
                [placeholder]="'trainer.contactPhonePlaceholder' | translate"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeApplicationModal()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-primary" (click)="submitApplication()" [disabled]="submittingApplication">
            @if (submittingApplication) {
              <span class="spinner-border spinner-border-sm me-2"></span>
            }
            {{ 'trainer.submitApplication' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Recommendation Modal -->
@if (showRecommendModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-user-friends me-2 text-success"></i>
            {{ 'trainer.recommendTitle' | translate }}
          </h5>
          <button type="button" class="btn-close" (click)="closeRecommendModal()"></button>
        </div>
        <div class="modal-body">
          <!-- Trainer Name -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.trainerName' | translate }} <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="recommendForm.trainer_name"
              [placeholder]="'trainer.trainerNamePlaceholder' | translate"
            />
          </div>

          <!-- Why Recommend -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.whyRecommend' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="4"
              [(ngModel)]="recommendForm.description"
              [placeholder]="'trainer.whyRecommendPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Specializations -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <div class="specializations-selector mb-2">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isRecommendSpecializationSelected(spec)"
                  (click)="toggleRecommendSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
          </div>

          <!-- Contact Info -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.contactInfo' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="recommendForm.contact_info"
              [placeholder]="'trainer.contactInfoPlaceholder' | translate"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeRecommendModal()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-success" (click)="submitRecommendation()" [disabled]="submittingRecommendation">
            @if (submittingRecommendation) {
              <span class="spinner-border spinner-border-sm me-2"></span>
            }
            {{ 'trainer.submitRecommendation' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}
